import { Component, Input, Output, EventEmitter, OnInit, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { CdkDragDrop, DragDropModule, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';

// PrimeNG Imports
import { SharedModule } from 'src/app/private/shared/shared.module';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextarea } from 'primeng/inputtextarea';
import { DropdownModule } from 'primeng/dropdown';
import { CheckboxModule } from 'primeng/checkbox';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { TooltipModule } from 'primeng/tooltip';
import { InputSwitchModule } from 'primeng/inputswitch';
import { TabsModule } from 'primeng/tabs';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';

import { DynamicForm, FormFieldConfig, Module } from '../models/form-builder.model';

interface FieldRow {
  id: string;
  columns: number; // 1, 2, or 3
  fields: FormFieldConfig[];
}

@Component({
  selector: 'app-create-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SharedModule,
    ButtonModule,
    InputTextModule,
    InputTextarea,
    DropdownModule,
    CheckboxModule,
    CardModule,
    DividerModule,
    TooltipModule,
    InputSwitchModule,
    TabsModule,
    DialogModule,
    InputNumberModule,
    DragDropModule
  ],
  templateUrl: './create-form.component.html',
  styleUrls: ['./create-form.component.scss']
})
export class CreateFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() form!: DynamicForm;
  @Input() availableFields: any[] = [];
  @Input() availableModules: Module[] = [];
  @Input() isEditMode: boolean = false;
  @Input() loadingFields: boolean = false;
  @Input() savingForm: boolean = false;

  @Output() onSave = new EventEmitter<DynamicForm>();
  @Output() onCancel = new EventEmitter<void>();
  @Output() onModuleChange = new EventEmitter<string>();

  // Form rows with configurable columns
  formRows: FieldRow[] = [];
  
  // Selected field for configuration
  selectedField: FormFieldConfig | null = null;
  showFieldConfigDialog = false;
  
  // Label editing
  showLabelEditDialog = false;
  labelEditField: FormFieldConfig | null = null;
  labelEditValue: string = '';
  
  // Active tab (0: Basic Info, 1: Form Builder)
  activeTab: number = 0;
  
  // Connected drop list IDs for drag-drop
  connectedDropListIds: string[] = [];
  
  // Column options
  columnOptions = [
    { label: '1 Column', value: 1 },
    { label: '2 Columns', value: 2 },
    { label: '3 Columns', value: 3 }
  ];

  // UI Type options
  uiTypeOptions = [
    { label: 'Text Input', value: 'text' },
    { label: 'Textarea', value: 'textarea' },
    { label: 'Number', value: 'number' },
    { label: 'Email', value: 'email' },
    { label: 'Date', value: 'date' },
    { label: 'Select Dropdown', value: 'select' },
    { label: 'Radio Buttons', value: 'radio' },
    { label: 'Checkbox', value: 'checkbox' },
    { label: 'Multi Select', value: 'multiselect' }
  ];

  constructor(private messageService: MessageService) {}

  ngOnInit(): void {
    this.initializeFormRows();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reset and reinitialize rows when form input changes
    if (changes['form']) {
      // Always reset internal state when form changes, regardless of firstChange
      this.resetComponentState();
      // Reinitialize form rows
      this.initializeFormRows();
    }
  }

  ngOnDestroy(): void {
    // Clean up state when component is destroyed
    this.resetComponentState();
  }

  resetComponentState(): void {
    // Clear all local state
    this.formRows = [];
    this.selectedField = null;
    this.showFieldConfigDialog = false;
    this.showLabelEditDialog = false;
    this.labelEditField = null;
    this.labelEditValue = '';
    this.activeTab = 0;
    this.connectedDropListIds = [];
  }

  initializeFormRows(): void {
    if (this.form.fields && this.form.fields.length > 0) {
      // Convert existing fields to rows
      this.convertFieldsToRows();
    } else {
      // Start with one empty row
      this.addNewRow(2);
    }
    this.updateConnectedDropLists();
  }

  convertFieldsToRows(): void {
    this.formRows = [];
    
    // Check if fields have row/col information
    const hasRowInfo = this.form.fields.some(f => f.row !== undefined);
    
    if (hasRowInfo) {
      // Reconstruct rows from row/col information
      const rowMap = new Map<number, FormFieldConfig[]>();
      
      // Group fields by row
      for (const field of this.form.fields) {
        const rowIndex = field.row !== undefined ? field.row : 0;
        if (!rowMap.has(rowIndex)) {
          rowMap.set(rowIndex, []);
        }
        rowMap.get(rowIndex)!.push(field);
      }
      
      // Sort rows by index and create FieldRow objects
      const sortedRowIndices = Array.from(rowMap.keys()).sort((a, b) => a - b);
      
      for (const rowIndex of sortedRowIndices) {
        const fields = rowMap.get(rowIndex)!;
        // Sort fields by col within the row
        fields.sort((a, b) => {
          const colA = a.col !== undefined ? a.col : 0;
          const colB = b.col !== undefined ? b.col : 0;
          return colA - colB;
        });
        
        // Get column count from first field's row_cols property, or calculate from field count
        let columnCount = 2; // Default to 2 columns
        
        if (fields.length > 0 && fields[0].row_cols !== undefined) {
          // Use the stored column count from when form was saved
          columnCount = fields[0].row_cols;
        } else {
          // Fallback: Use field count or minimum 2
          columnCount = Math.max(fields.length, 2);
        }
        
        this.formRows.push({
          id: this.generateRowId(),
          columns: columnCount,
          fields: fields
        });
      }
    } else {
      // Legacy: Group fields by order, default to 2 columns per row
      const fieldsPerRow = 2;
      
      for (let i = 0; i < this.form.fields.length; i += fieldsPerRow) {
        const rowFields = this.form.fields.slice(i, i + fieldsPerRow);
        this.formRows.push({
          id: this.generateRowId(),
          columns: fieldsPerRow,
          fields: rowFields
        });
      }
    }
  }

  // ============ Module Selection ============
  
  onModuleSelected(): void {
    console.log("Module selected:", this.form.module_name);
    console.log("Form:", this.form);
    this.onModuleChange.emit(this.form.module_name);
    // Do not auto-switch tabs - user must click "Next: Build Form" button
  }

  // ============ Row Management ============
  
  addNewRow(columns: number = 2): void {
    this.formRows.push({
      id: this.generateRowId(),
      columns: columns,
      fields: []
    });
    this.updateConnectedDropLists();
  }

  deleteRow(index: number): void {
    this.formRows.splice(index, 1);
    this.updateConnectedDropLists();
  }

  changeRowColumns(row: FieldRow, newColumns: number): void {
    row.columns = newColumns;
    // If reducing columns, move excess fields to next row
    if (row.fields.length > newColumns) {
      const excessFields = row.fields.splice(newColumns);
      
      // Find next row or create one
      const rowIndex = this.formRows.indexOf(row);
      if (rowIndex < this.formRows.length - 1) {
        this.formRows[rowIndex + 1].fields.unshift(...excessFields);
      } else {
        this.formRows.push({
          id: this.generateRowId(),
          columns: newColumns,
          fields: excessFields
        });
      }
    }
  }

  moveRowUp(index: number): void {
    if (index > 0) {
      const temp = this.formRows[index];
      this.formRows[index] = this.formRows[index - 1];
      this.formRows[index - 1] = temp;
    }
  }

  moveRowDown(index: number): void {
    if (index < this.formRows.length - 1) {
      const temp = this.formRows[index];
      this.formRows[index] = this.formRows[index + 1];
      this.formRows[index + 1] = temp;
    }
  }

  // ============ Drag & Drop ============
  
  onFieldDropped(event: CdkDragDrop<any[]>, row: FieldRow): void {
    console.log('Drop event:', {
      previousContainer: event.previousContainer.id,
      currentContainer: event.container.id,
      previousIndex: event.previousIndex,
      currentIndex: event.currentIndex
    });

    if (event.previousContainer === event.container) {
      // Reordering within same row
      moveItemInArray(row.fields, event.previousIndex, event.currentIndex);
    } else if (event.previousContainer.id === 'available-fields') {
      // Dropping from available fields
      const field = event.previousContainer.data[event.previousIndex];
      
      console.log('Field being dropped:', field);
      
      // Check if field already exists
      const exists = this.formRows.some(r => 
        r.fields.some(f => this.getFieldId(f) === this.getFieldId(field))
      );

      if (exists) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Warning',
          detail: 'Field already added to form'
        });
        return;
      }

      // Check if row is full
      if (row.fields.length >= row.columns) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Warning',
          detail: `Row is full (${row.columns} columns). Add a new row or change row layout.`
        });
        return;
      }

      // Create field config from available field
      const fieldConfig = this.createFieldConfig(field, row.fields.length);
      row.fields.push(fieldConfig);
      
      // Force array reference update for change detection
      row.fields = [...row.fields];
      
      console.log('Field added successfully:', fieldConfig);
    } else {
      // Moving between rows
      if (row.fields.length >= row.columns) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Warning',
          detail: `Row is full (${row.columns} columns)`
        });
        return;
      }
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    }

    this.updateFieldOrders();
  }

  createFieldConfig(field: any, order: number): FormFieldConfig {
    // Handle both fixed fields and dynamic fields
    const fieldId = field.field_id !== undefined ? field.field_id : (field.id || field.field);
    let dataType = field.data_type || field.type;
    
    // If no data type provided, infer from field properties
    if (!dataType || dataType === 'undefined') {
      // Check if field has options/data - likely enum
      if ((field.options && field.options.length > 0) || (field.data && field.data.length > 0)) {
        dataType = 'enum';
      } else {
        dataType = 'string';
      }
    }
    
    // Create a properly structured Field object
    const fieldObj: any = {
      id: fieldId,
      field_name: field.field_name || field.name || field.field || 'field',
      label: field.label || field.name || field.field_name || 'Field',
      data_type: dataType,
      // module_name: field.module_name || this.form.module_name || '',
      module_name: field.module_name || '',
      is_required: field.required === true || field.required === 'true' || field.is_required === true,
      is_system_field: field.is_system_field || false,
      options: field.options || field.data || []
    };

    // Check if field already has a ui_type (from dynamic fields API), otherwise derive from data type
    let uiType: string;
    if (field.ui_type && this.isValidUIType(field.ui_type)) {
      // Use the UI type from the field if it exists and is valid
      uiType = field.ui_type;
    } else {
      // Fall back to deriving from data type
      uiType = this.getDefaultUIType(dataType);
    }
    
    // Determine field_type based on field source: static (predefined fields) or dynamic (API fields)
    const fieldType: 'static' | 'dynamic' = field._field_source === 'dynamic' ? 'dynamic' : 'static';

    return {
      field_id: fieldId,
      field: fieldObj,
      ui_type: uiType,
      is_required: fieldObj.is_required,
      placeholder: field.placeholder || '',
      help_text: '',
      order: order,
      options: fieldObj.options,
      field_type: fieldType
    };
  }

  isValidUIType(uiType: string): boolean {
    const validTypes = ['text', 'textarea', 'number', 'email', 'tel', 'date', 'checkbox', 'select', 'radio', 'multiselect'];
    return validTypes.includes(uiType);
  }

  getDefaultUIType(dataType: string): string {
    const mapping: { [key: string]: string } = {
      'string': 'text',
      'text': 'text',
      'number': 'number',
      'email': 'email',
      'tel': 'tel',
      'date': 'date',
      'boolean': 'checkbox',
      'enum': 'select'
    };
    return mapping[dataType] || 'text';
  }

  // ============ Field Configuration ============
  
  configureField(field: FormFieldConfig): void {
    // Deep copy the field
    this.selectedField = JSON.parse(JSON.stringify(field));
    
    if (this.selectedField) {
      // Ensure all required properties exist
      if (!this.selectedField.ui_type) {
        this.selectedField.ui_type = this.getDefaultUIType(this.selectedField.field?.data_type || 'string');
      }
      if (!this.selectedField.options || !Array.isArray(this.selectedField.options)) {
        this.selectedField.options = [];
      }
      if (!this.selectedField.placeholder) {
        this.selectedField.placeholder = '';
      }
      if (!this.selectedField.help_text) {
        this.selectedField.help_text = '';
      }
      // Ensure field.data_type exists
      if (this.selectedField.field && !this.selectedField.field.data_type) {
        this.selectedField.field.data_type = this.inferDataTypeFromUIType(this.selectedField.ui_type);
      }
    }
    
    this.showFieldConfigDialog = true;
  }

  editFieldLabel(field: FormFieldConfig): void {
    this.labelEditField = field;
    this.labelEditValue = this.getFieldLabel(field.field) || '';
    this.showLabelEditDialog = true;
  }

  saveFieldLabel(): void {
    if (this.labelEditField && this.labelEditValue.trim()) {
      // Update the label in the field object
      if (typeof this.labelEditField.field === 'object' && this.labelEditField.field !== null) {
        this.labelEditField.field.label = this.labelEditValue;
      }
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Field label updated'
      });
    }
    this.showLabelEditDialog = false;
    this.labelEditField = null;
    this.labelEditValue = '';
  }

  saveFieldConfig(): void {
    if (this.selectedField) {
      // Ensure data type exists
      if (this.selectedField.field && !this.selectedField.field.data_type) {
        this.selectedField.field.data_type = this.inferDataTypeFromUIType(this.selectedField.ui_type);
      }
      
      // Find and update the field in rows
      for (const row of this.formRows) {
        const index = row.fields.findIndex(f => 
          this.getFieldId(f) === this.getFieldId(this.selectedField!)
        );
        if (index !== -1) {
          // Create a new object to trigger change detection
          const updatedField = { ...this.selectedField };
          row.fields[index] = updatedField;
          
          // Force array reference update for change detection
          row.fields = [...row.fields];
          break;
        }
      }
      
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Field configuration saved'
      });
    }
    
    this.showFieldConfigDialog = false;
    this.selectedField = null;
  }

  removeField(row: FieldRow, field: FormFieldConfig): void {
    const index = row.fields.indexOf(field);
    if (index !== -1) {
      row.fields.splice(index, 1);
      // Force array reference update for change detection
      row.fields = [...row.fields];
    }
    this.updateFieldOrders();
  }

  // Options management methods
  getFieldOptions(field: FormFieldConfig): any[] {
    if (!field.options || !Array.isArray(field.options)) {
      field.options = [];
    }
    return field.options;
  }

  addOption(): void {
    if (this.selectedField) {
      if (!this.selectedField.options || !Array.isArray(this.selectedField.options)) {
        this.selectedField.options = [];
      }
      this.selectedField.options.push('');
    }
  }

  removeOption(index: number): void {
    if (this.selectedField && this.selectedField.options && Array.isArray(this.selectedField.options)) {
      this.selectedField.options.splice(index, 1);
    }
  }

  updateOption(index: number, event: any): void {
    if (this.selectedField && this.selectedField.options && Array.isArray(this.selectedField.options)) {
      this.selectedField.options[index] = event.target.value;
    }
  }

  updateFieldOrders(): void {
    let order = 0;
    for (let rowIndex = 0; rowIndex < this.formRows.length; rowIndex++) {
      const row = this.formRows[rowIndex];
      for (let colIndex = 0; colIndex < row.fields.length; colIndex++) {
        const field = row.fields[colIndex];
        field.order = order++;
        field.row = rowIndex;
        field.col = colIndex;
      }
    }
  }

  // ============ Form Submission ============
  
  saveForm(): void {
    // Validation
    if (!this.form.form_name || !this.form.module_name) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please fill in form name and select a module'
      });
      return;
    }

    // Flatten rows into fields array with row and column position
    this.form.fields = [];
    let globalOrder = 0;
    
    for (let rowIndex = 0; rowIndex < this.formRows.length; rowIndex++) {
      const row = this.formRows[rowIndex];
      
      for (let colIndex = 0; colIndex < row.fields.length; colIndex++) {
        const field = row.fields[colIndex];
        
        // Add row and column position to each field
        field.row = rowIndex;
        field.col = colIndex;
        field.order = globalOrder++;
        
        // Store column count on the first field of each row
        if (colIndex === 0) {
          field.row_cols = row.columns;
        } else {
          // Clear row_cols for non-first fields
          delete field.row_cols;
        }
        
        this.form.fields.push(field);
      }
    }

    if (this.form.fields.length === 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please add at least one field to the form'
      });
      return;
    }

    // Ensure all fields have a data_type set
    for (const field of this.form.fields) {
      if (field.field && !field.field.data_type) {
        field.field.data_type = this.inferDataTypeFromUIType(field.ui_type);
      }
    }

    // Emit save event with updated fields (parent component handles publish state)
    this.onSave.emit(this.form);
  }

  cancel(): void {
    this.onCancel.emit();
  }

  validateFieldsCompatibility(): string[] {
    const errors: string[] = [];

    // Very lenient compatibility - only check truly incompatible combinations
    const strictlyIncompatible: { [key: string]: string[] } = {
      'number': ['string', 'text', 'enum', 'boolean', 'date'],
      'checkbox': ['string', 'text', 'enum', 'number', 'date'],
      'date': ['string', 'text', 'enum', 'number', 'boolean']
    };

    for (const field of this.form.fields) {
      // Auto-fix data type if missing
      if (!field.field?.data_type) {
        if (field.field) {
          field.field.data_type = this.inferDataTypeFromUIType(field.ui_type);
        }
      }
      
      const dataType = field.field?.data_type || 'string';
      const uiType = field.ui_type;
      
      // For select, radio, multiselect - allow any string-like type
      if (['select', 'radio', 'multiselect'].includes(uiType)) {
        if (['string', 'text', 'enum'].includes(dataType)) {
          // These are all compatible - skip validation
          continue;
        }
      }
      
      // Check only strictly incompatible combinations
      const incompatibleTypes = strictlyIncompatible[uiType] || [];
      if (incompatibleTypes.includes(dataType)) {
        const fieldLabel = field.field?.label || field.field?.field_name || 'Unknown';
        errors.push(`Field "${fieldLabel}": UI type "${uiType}" cannot be used with data type "${dataType}"`);
      }
    }

    return errors;
  }

  inferDataTypeFromUIType(uiType: string): 'string' | 'number' | 'boolean' | 'date' | 'enum' | 'text' {
    const uiToDataTypeMap: { [key: string]: 'string' | 'number' | 'boolean' | 'date' | 'enum' | 'text' } = {
      'text': 'string',
      'textarea': 'text',
      'number': 'number',
      'email': 'string',
      'tel': 'string',
      'date': 'date',
      'checkbox': 'boolean',
      'select': 'enum',
      'radio': 'enum',
      'multiselect': 'enum'
    };
    return uiToDataTypeMap[uiType] || 'string';
  }

  isUITypeCompatibleWithDataType(uiType: string, dataType: string): boolean {
    // Very lenient compatibility check
    // For select/radio/multiselect, allow string, text, or enum
    if (['select', 'radio', 'multiselect'].includes(uiType)) {
      return ['string', 'text', 'enum'].includes(dataType);
    }
    
    // For text inputs, allow string or text
    if (['text', 'textarea', 'email', 'tel'].includes(uiType)) {
      return ['string', 'text'].includes(dataType);
    }
    
    // Strict checks for specific types
    if (uiType === 'number') return dataType === 'number';
    if (uiType === 'checkbox') return dataType === 'boolean';
    if (uiType === 'date') return dataType === 'date';
    
    // Default: allow
    return true;
  }

  // ============ Helpers ============
  
  getFieldId(field: any): string {
    return field.field_id !== undefined ? field.field_id.toString() : field.field;
  }

  getFieldLabel(field: any): string {
    return field.field?.label || field.label || field.field_name || 'Unknown Field';
  }

  generateRowId(): string {
    return 'row-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
  }

  getRowDropZoneId(rowIndex: number): string {
    return 'field-canvas-' + rowIndex;
  }

  updateConnectedDropLists(): void {
    this.connectedDropListIds = this.formRows.map((_, index) => this.getRowDropZoneId(index));
  }

  getRowFields(row: FieldRow): FormFieldConfig[] {
    return row.fields;
  }

  showOptions(field: FormFieldConfig): boolean {
    return ['select', 'radio', 'multiselect'].includes(field.ui_type);
  }

  getModuleName(moduleId: string): string {
    const module = this.availableModules.find(m => m.id === moduleId);
    return module ? module.name : 'Unknown';
  }

  // Track by functions for performance (arrow functions to preserve 'this' context)
  trackByRowId = (index: number, row: FieldRow): string => {
    return row.id;
  };

  trackByFieldId = (index: number, field: any): string => {
    return this.getFieldId(field);
  };

  trackByIndex = (index: number): number => {
    return index;
  };

  getTotalFields(): number {
    return this.formRows.reduce((total, row) => total + row.fields.length, 0);
  }
}
